import React, { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { supabase } from '@/integrations/supabase/client'
import { Webhook, CheckCircle, XCircle, Clock, AlertTriangle, RefreshCw, ExternalLink } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import { WebhookDetailModal } from './WebhookDetailModal'
import { smartleadAPI } from '@/lib/smartlead-api'
import { DEMO_WEBHOOK_EVENTS, getDemoWebhookStats } from '@/data/demoWebhookEvents'

interface WebhookLog {
  id: string
  created_at: string
  title: string
  description: string
  activity_type: string
  icon: string
}

export function WebhookMonitor() {
  const [webhookLogs, setWebhookLogs] = useState<WebhookLog[]>([])
  const [loading, setLoading] = useState(true)
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date())
  const [selectedWebhook, setSelectedWebhook] = useState<WebhookLog | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const { toast } = useToast()

  const fetchWebhookLogs = async () => {
    try {
      setLoading(true)
      
      // Check if Smartlead is configured - if not, use demo data
      if (!smartleadAPI.isConfigured()) {
        console.log('🔍 [WebhookMonitor] Smartlead API not configured, using demo webhook events...');
        
        // Convert demo webhook events to WebhookLog format
        const demoWebhookLogs: WebhookLog[] = DEMO_WEBHOOK_EVENTS.map(event => ({
          id: event.id,
          created_at: event.created_at,
          title: event.title,
          description: event.description,
          activity_type: event.activity_type,
          icon: event.icon
        }));

        setWebhookLogs(demoWebhookLogs);
        setLastUpdate(new Date());
        setLoading(false);
        return;
      }
      
      // Query recent activity for webhook events
      const { data, error } = await supabase
        .from('recent_activity')
        .select('*')
        .or('activity_type.eq.webhook_success,activity_type.eq.webhook_error,activity_type.eq.webhook_received')
        .order('created_at', { ascending: false })
        .limit(1000)

      if (error) {
        console.error('Error fetching webhook logs:', error)
        toast({
          title: "Error",
          description: "Failed to fetch webhook logs",
          variant: "destructive"
        })
        return
      }

      setWebhookLogs(data || [])
      setLastUpdate(new Date())
    } catch (error) {
      console.error('Error in fetchWebhookLogs:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchWebhookLogs()
    
    // Set up auto-refresh every 30 seconds (only in real mode)
    let interval: NodeJS.Timeout | null = null;
    if (smartleadAPI.isConfigured()) {
      interval = setInterval(fetchWebhookLogs, 30000);
    }
    
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [])

  const getStatusColor = (activityType: string) => {
    if (activityType === 'webhook_error') return 'destructive'
    if (activityType === 'webhook_success') return 'default'
    return 'secondary'
  }

  const getStatusIcon = (activityType: string) => {
    switch (activityType) {
      case 'webhook_success':
        return <CheckCircle className="w-4 h-4 text-green-500" />
      case 'webhook_error':
        return <XCircle className="w-4 h-4 text-red-500" />
      default:
        return <Clock className="w-4 h-4 text-yellow-500" />
    }
  }

  const formatTimestamp = (timestamp: string) => {
    try {
      const date = new Date(timestamp)
      return date.toLocaleString()
    } catch {
      return 'Unknown'
    }
  }

  const parseWebhookDetails = (description: string) => {
    const details: Record<string, string> = {}
    const parts = description.split(' | ')
    
    parts.forEach(part => {
      const [key, value] = part.split(': ')
      if (key && value) {
        details[key.trim()] = value.trim()
      }
    })
    
    return details
  }

  const handleWebhookClick = (webhook: WebhookLog) => {
    setSelectedWebhook(webhook)
    setIsModalOpen(true)
  }

  const getShortDescription = (description: string, type: string) => {
    if (type === 'webhook_success' || type === 'webhook_error') {
      const details = parseWebhookDetails(description)
      const to = details.TO ? `To: ${details.TO.split('@')[0]}...` : ''
      const category = details.CATEGORY ? ` | ${details.CATEGORY}` : ''
      return to + category
    }
    return description.length > 60 ? description.substring(0, 60) + '...' : description
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div className="flex items-center gap-2">
          <Webhook className="w-5 h-5 text-primary" />
          <CardTitle>Webhook Monitor</CardTitle>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">
            Last updated: {lastUpdate.toLocaleTimeString()}
          </span>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={fetchWebhookLogs}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>
      
      <CardContent>
        {loading && webhookLogs.length === 0 ? (
          <div className="text-center py-8">
            <div className="animate-spin mx-auto w-6 h-6 border-2 border-primary border-t-transparent rounded-full"></div>
            <p className="text-sm text-muted-foreground mt-2">Loading webhook logs...</p>
          </div>
        ) : webhookLogs.length === 0 ? (
          <div className="text-center py-8">
            <AlertTriangle className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">No webhook logs found</p>
          </div>
        ) : (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {webhookLogs.map((log) => (
              <div 
                key={log.id} 
                className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                onClick={() => handleWebhookClick(log)}
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  {getStatusIcon(log.activity_type)}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-sm font-medium truncate">
                        {log.title}
                      </p>
                      <ExternalLink className="w-3 h-3 text-muted-foreground shrink-0" />
                    </div>
                    <p className="text-xs text-muted-foreground truncate">
                      {getShortDescription(log.description, log.activity_type)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatTimestamp(log.created_at)}
                    </p>
                  </div>
                </div>
                
                <Badge variant={getStatusColor(log.activity_type)} className="shrink-0">
                  {log.activity_type.replace('webhook_', '')}
                </Badge>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <WebhookDetailModal 
        webhook={selectedWebhook}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false)
          setSelectedWebhook(null)
        }}
      />
    </Card>
  )
}